import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/ui/Button';
import Chip from '../components/ui/Chip';
import Input from '../components/ui/Input';
import API from '../services/api';
import { INTERESTS, RELATIONSHIP_GOALS } from '../data/demo';
import { toast } from 'react-toastify';
import { FiCamera, FiX, FiArrowLeft } from 'react-icons/fi';

const STEPS = ['Photos', 'About You', 'Details', 'Interests', 'Goals', 'Prompts'];

const ProfileSetup = () => {
  const [step, setStep] = useState(0);
  const [photos, setPhotos] = useState([]);
  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [city, setCity] = useState('');
  const [profession, setProfession] = useState('');
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [selectedGoal, setSelectedGoal] = useState('');
  const [promptAnswer, setPromptAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();
  const navigate = useNavigate();

  const toggleInterest = (i) => {
    setSelectedInterests(p => p.includes(i) ? p.filter(x => x !== i) : p.length < 8 ? [...p, i] : p);
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > 6) { toast.error('Max 6 photos'); return; }
    const formData = new FormData();
    files.forEach(f => formData.append('photos', f));
    try {
      const res = await API.post('/auth/photos', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPhotos(p => [...p, ...res.data.photos]);
      toast.success('Photo added!');
    } catch { toast.error('Upload failed'); }
  };

  const removePhoto = (idx) => setPhotos(p => p.filter((_, i) => i !== idx));

  const handleComplete = async () => {
    setLoading(true);
    try {
      await API.put('/auth/profile', {
        name, bio, city, interests: selectedInterests,
        preferences: { relationshipGoal: selectedGoal, promptAnswer }
      });
      toast.success('Profile complete!');
      navigate('/discover');
    } catch { toast.error('Failed to save'); }
    finally { setLoading(false); }
  };

  const renderStep = () => {
    switch (step) {
      case 0: return (
        <div>
          <p style={{ color: '#71717A', fontSize: 14, marginBottom: 20 }}>Add your best photos. First photo is your main profile picture.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            {photos.map((p, i) => (
              <div key={i} style={{ aspectRatio: 3/4, borderRadius: 16, overflow: 'hidden', position: 'relative' }}>
                <img src={`https://rt.vitaldew.in${p}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <button onClick={() => removePhoto(i)} style={{
                  position: 'absolute', top: 6, right: 6, width: 24, height: 24,
                  borderRadius: 12, background: 'rgba(0,0,0,0.6)', border: 'none',
                  color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}><FiX size={12} /></button>
                {i === 0 && <div style={{
                  position: 'absolute', bottom: 6, left: 6, padding: '3px 8px',
                  background: 'linear-gradient(135deg, #FF3D71, #8B5CF6)', borderRadius: 6,
                  fontSize: 10, fontWeight: 600, color: 'white',
                }}>PRIMARY</div>}
              </div>
            ))}
            {photos.length < 6 && (
              <button onClick={() => fileRef.current.click()} style={{
                aspectRatio: 3/4, borderRadius: 16, border: '2px dashed rgba(255,255,255,0.1)',
                background: 'rgba(255,255,255,0.02)', color: '#71717A',
                cursor: 'pointer', display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 8,
              }}><FiCamera size={24} /><span style={{ fontSize: 12 }}>Add Photo</span></button>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={handlePhotoUpload} />
        </div>
      );
      case 1: return (
        <div>
          <Input label="Display Name" value={name} onChange={e => setName(e.target.value)} placeholder="Your name" />
          <Input label="About Me" value={bio} onChange={e => setBio(e.target.value)}
            placeholder="Tell people about yourself..." textarea rows={4} />
          <Input label="City" value={city} onChange={e => setCity(e.target.value)} placeholder="Where do you live?" />
          <Input label="Profession" value={profession} onChange={e => setProfession(e.target.value)} placeholder="What do you do?" />
        </div>
      );
      case 2: return (
        <div>
          <Input label="Education" placeholder="Your education" />
          <Input label="Relationship Status" placeholder="Current status" />
        </div>
      );
      case 3: return (
        <div>
          <p style={{ color: '#71717A', fontSize: 14, marginBottom: 16 }}>Select up to 8 interests that define you</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {INTERESTS.map(i => (
              <Chip key={i} label={i} selected={selectedInterests.includes(i)} onClick={() => toggleInterest(i)} />
            ))}
          </div>
          <p style={{ fontSize: 12, color: '#52525B', marginTop: 12 }}>{selectedInterests.length}/8 selected</p>
        </div>
      );
      case 4: return (
        <div>
          <p style={{ color: '#71717A', fontSize: 14, marginBottom: 16 }}>What are you looking for?</p>
          {RELATIONSHIP_GOALS.map(g => (
            <button key={g.id} onClick={() => setSelectedGoal(g.id)} style={{
              display: 'flex', alignItems: 'center', gap: 14, width: '100%',
              padding: '16px', marginBottom: 10, borderRadius: 16,
              background: selectedGoal === g.id ? 'rgba(255,61,113,0.1)' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${selectedGoal === g.id ? 'rgba(255,61,113,0.3)' : 'rgba(255,255,255,0.06)'}`,
              cursor: 'pointer', fontFamily: 'Inter', textAlign: 'left', transition: 'all 0.2s',
            }}>
              <span style={{ fontSize: 28 }}>{g.icon}</span>
              <div>
                <h4 style={{ fontSize: 15, fontWeight: 600, color: '#FFFFFF', marginBottom: 2 }}>{g.label}</h4>
                <p style={{ fontSize: 12, color: '#71717A' }}>{g.desc}</p>
              </div>
            </button>
          ))}
        </div>
      );
      case 5: return (
        <div>
          <div style={{
            padding: '20px', borderRadius: 20, marginBottom: 16,
            background: 'rgba(255,61,113,0.06)', border: '1px solid rgba(255,61,113,0.15)',
          }}>
            <p style={{ fontSize: 13, color: '#FF3D71', fontWeight: 600, marginBottom: 8 }}>My perfect Sunday is...</p>
            <Input value={promptAnswer} onChange={e => setPromptAnswer(e.target.value)}
              placeholder="Write your answer..." textarea rows={3} />
          </div>
        </div>
      );
      default: return null;
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#08080C', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
        {step > 0 && (
          <button onClick={() => setStep(s => s - 1)} style={{
            background: 'none', border: 'none', color: '#A1A1AA', cursor: 'pointer', padding: 4,
          }}><FiArrowLeft size={22} /></button>
        )}
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700 }}>{STEPS[step]}</h2>
          <p style={{ fontSize: 12, color: '#71717A' }}>Step {step + 1} of {STEPS.length}</p>
        </div>
      </div>

      <div style={{ padding: '0 20px 8px', display: 'flex', gap: 4 }}>
        {STEPS.map((_, i) => (
          <div key={i} style={{ flex: 1, height: 3, borderRadius: 2,
            background: i <= step ? 'linear-gradient(135deg, #FF3D71, #8B5CF6)' : 'rgba(255,255,255,0.06)' }} />
        ))}
      </div>

      <div style={{ flex: 1, padding: '20px', overflowY: 'auto' }}>
        {renderStep()}
      </div>

      <div style={{ padding: '16px 20px 32px' }}>
        <Button onClick={() => step < STEPS.length - 1 ? setStep(s => s + 1) : handleComplete()}
          fullWidth size="lg" disabled={loading}>
          {step < STEPS.length - 1 ? 'Continue' : loading ? 'Saving...' : 'Complete Profile'}
        </Button>
      </div>
    </div>
  );
};

export default ProfileSetup;
